import { cloneDeep } from 'lodash-es';
import { Pipe, PipeTransform } from '@angular/core';
import { PriceConfig } from '../../../types';
import { select, Store } from '@ngrx/store';
import {
  listFilePriceEntitlements,
  PrintInBasket,
  ShoppingCartState,
} from '../../actions/standalone';
import {
  selectFilePrices,
  selectFilesInBasket,
  selectPackagesInBasket,
  selectPhotoGroups,
  selectPhotosList,
  selectPriceToAddToEveryFileInProduct,
  selectPriceToAddToFirstPrint,
  selectPrintsInBasket,
  selectProductsInBasket,
} from '../../actions/selector';
import { combineLatest, map, switchMap } from 'rxjs';

import { singleStepPriceCalculator } from '@ui/step-pricing/StepPriceCalculator';
import { StepPricingMode } from '../../../../../../../../photographer-panel/src/graphql';

@Pipe({
  name: 'priceToAddToFirstPrint',
  standalone: false,
})
export class PriceToAddToFirstPrintPipe implements PipeTransform {
  constructor(private store: Store<{ shoppingCart: ShoppingCartState }>) {}

  transform(a: Array<PriceConfig>, print: PrintInBasket) {
    const price = cloneDeep(a);
    return combineLatest([
      this.store
        .pipe(select(selectPhotosList))
        .pipe(
          switchMap((photosList) =>
            this.store
              .pipe(select(selectPhotoGroups))
              .pipe(
                map(
                  (photoGroup) =>
                    photoGroup.find(
                      (p) =>
                        p.photoGroupId ===
                        photosList
                          .find(({ photos }) =>
                            photos.find(
                              ({ photoId }) => photoId === print.photoId,
                            ),
                          )
                          ?.photos.find(
                            ({ photoId }) => photoId === print.photoId,
                          )?.photoGroupId,
                    )?.config,
                ),
              ),
          ),
        ),
      this.store
        .pipe(select(selectPrintsInBasket))
        .pipe(
          map((printsInBasket) =>
            printsInBasket.filter(({ photoId }) => photoId === print.photoId),
          ),
        ),
      this.store.pipe(select(selectFilesInBasket)),
      this.store.pipe(select(selectPackagesInBasket)),
      combineLatest([
        this.store.pipe(select(selectPriceToAddToFirstPrint)),
        this.store.pipe(select(selectFilePrices)),
        this.store.pipe(select(selectPrintsInBasket)),
      ]),
      combineLatest([
        this.store.pipe(select(selectProductsInBasket)),
        this.store.pipe(select(selectPriceToAddToEveryFileInProduct)),
        this.store.pipe(select(selectPhotosList)),
        this.store.pipe(select(selectPhotoGroups)),
      ]),
    ]).pipe(
      map(
        ([
          config,
          filterPrintsInBasket,
          filesInBasket,
          packagesInBasket,
          [priceToAddToFirstPrint, filePrices, printsInBasket],
          [
            productsInBasket,
            priceToAddToEveryFileInProduct,
            photosList,
            photoGroups,
          ],
        ]) => {
          if (
            filterPrintsInBasket[0]?.printFormatId === print.printFormatId &&
            filterPrintsInBasket[0]?.printPaperId === print.printPaperId &&
            !filterPrintsInBasket[0]?.fromOldBasket
          ) {
            const supplementPrint =
              config?.individualSellSettings && config?.sellPrints
                ? config.priceToAddToFirstPrint
                : priceToAddToFirstPrint;
            if (
              supplementPrint.addToFirstPrintPrice &&
              !filterPrintsInBasket.find(
                ({ photoId, fromOldBasket }) =>
                  photoId === print.photoId && fromOldBasket,
              ) &&
              ((!supplementPrint.skipAddToFirstPrintIfFileBought &&
                !(
                  filesInBasket.find(
                    ({ photoId, timeAddedToShoppingCart }) =>
                      timeAddedToShoppingCart < print.timeAddedToShoppingCart &&
                      photoId === print.photoId,
                  ) &&
                  filterPrintsInBasket.find(
                    ({ togetherWithFile, photoId }) =>
                      photoId === print.photoId && togetherWithFile,
                  )
                )) ||
                (supplementPrint.skipAddToFirstPrintIfFileBought &&
                  !packagesInBasket.find(({ file }) =>
                    file.find(({ photoId }) => photoId === print.photoId),
                  ) &&
                  ((supplementPrint.addPrintFileToBasketIfNotAlreadyBought &&
                    !filesInBasket.find(
                      (file) =>
                        !file?.togetherWithPrint &&
                        file.photoId === print.photoId,
                    )) ||
                    (!supplementPrint.addPrintFileToBasketIfNotAlreadyBought &&
                      !filesInBasket.find(
                        ({ photoId }) => photoId === print.photoId,
                      )))))
            ) {
              if (supplementPrint.countWithFilePrices) {
                const filesPrice =
                  config?.individualSellSettings && config.customFilesPricing
                    ? config.files
                    : filePrices;
                const filePriceEntitlements = listFilePriceEntitlements({
                  photoGroups,
                  photoGroupList: photosList,
                  priceToAddToFirstPrint,
                  priceToAddToEveryFileInProduct,
                  filePrices,
                  printsInBasket,
                  filesInBasket,
                  productsInBasket,
                  packagesInBasket,
                });
                const entitlement = filePriceEntitlements.find(
                  (item) =>
                    item.photoId === print.photoId && item.type === 'print',
                );
                if (!entitlement) {
                  return price;
                }
                const pricingList = filePriceEntitlements.filter(
                  (item) => item.filePricingId === entitlement.filePricingId,
                );
                const entitlementIndex = pricingList.findIndex(
                  (item) =>
                    item.photoId === print.photoId && item.type === 'print',
                );
                const filePrice = filesPrice.find(
                  ({ filePricingId }) =>
                    filePricingId === entitlement.filePricingId,
                );
                if (entitlementIndex < 0 || !filePrice) {
                  return price;
                }
                singleStepPriceCalculator(
                  entitlementIndex + 1,
                  pricingList.length,
                  !filePrice.stepPricingEnabled ||
                    filePrice.stepPricing.mode === 'None'
                    ? {
                        ...filePrice.stepPricing,
                        mode: StepPricingMode.None,
                      }
                    : filePrice.stepPricing,
                  filePrice.price,
                ).forEach((cost) => {
                  if (price.find(({ code }) => code === cost.code)) {
                    price.find(({ code }) => code === cost.code).price +=
                      cost.price;
                  } else {
                    price.push({ ...cost });
                  }
                });
              } else {
                price.forEach((p, i) => {
                  price[i].price += supplementPrint.priceToAddToFirstPrint.find(
                    (f) => f.code === p.code,
                  )?.price;
                });
              }
            }
          }
          return price;
        },
      ),
    );
  }
}
